if (!customElements.get(`after-hours-header`)) {
	class AfterHoursHeader extends HTMLElement {
		constructor() {
			super();

			this.main_logo = this.dataset.startingLogo || null;
			this.alt_logo = this.dataset.altLogo || null;

			this.trigger_el = document.querySelector(`[data-hero-svg]`);
			this.logo_wrapper = this.querySelector(`[data-main-bar-logo]`);
			this.logo_img = this.logo_wrapper
				? this.logo_wrapper.querySelector(`img`)
				: null;
		}

		applyOffset() {
			const main = document.querySelector(`main`);

			if (!main) return;

			main.style.marginTop = `${this.offsetHeight}px`;
		}

		setLogoSrc(new_src, { animate = true } = {}) {
			if (!this.logo_img || !new_src) return;

			const current_src = this.logo_img.getAttribute(`src`) || ``;
			if (current_src === new_src) return;

			if (!animate) {
				this.logo_img.style.opacity = `1`;
				this.logo_img.setAttribute(`src`, new_src);
				this.logo_img.removeAttribute(`srcset`);
				this.logo_img.removeAttribute(`sizes`);
				this.logo_img.style.height = `60px`;
				this.logo_img.style.width = `auto`;
				this.logo_img.style.objectFit = `contain`;
				return;
			}

			this.logo_img.style.opacity = `0`;

			const on_fade_out = () => {
				this.logo_img.removeEventListener(`transitionend`, on_fade_out);
				this.logo_img.setAttribute(`src`, new_src);
				this.logo_img.removeAttribute(`srcset`);
				this.logo_img.removeAttribute(`sizes`);
				this.logo_img.style.height = `60px`;
				this.logo_img.style.width = `auto`;
				this.logo_img.style.objectFit = `contain`;

				requestAnimationFrame(() => {
					this.logo_img.style.opacity = `1`;
				});
			};

			this.logo_img.addEventListener(`transitionend`, on_fade_out, {
				once: true,
			});
		}

		initLogoSwap() {
			if (
				!this.trigger_el ||
				!this.logo_img ||
				!this.main_logo ||
				!this.alt_logo
			) {
				return;
			}

			let has_initialized = false;

			const compute_src = (entry) => {
				if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
					return this.alt_logo;
				}
				return this.main_logo;
			};

			const observer = new IntersectionObserver(
				([entry]) => {
					const next_src = compute_src(entry);

					if (!has_initialized) {
						this.setLogoSrc(next_src, { animate: false });
						has_initialized = true;
						return;
					}

					this.setLogoSrc(next_src, { animate: true });
				},
				{ threshold: 0 }
			);

			observer.observe(this.trigger_el);
		}

		observeScroll() {
			const nav_links = Array.from(
				this.querySelectorAll(`.ah-main-bar-nav-item[href^="#"]`)
			);
			if (!nav_links.length) return;

			const get_header_offset = () =>
				this.getBoundingClientRect?.().height || this.offsetHeight || 0;

			const link_to_id = (link) => (link.getAttribute(`href`) || ``).slice(1);

			const section_items = nav_links
				.map((link) => {
					const target_id = link_to_id(link);
					const section_el = target_id
						? document.getElementById(target_id)
						: null;
					return section_el ? { link, section_el, target_id } : null;
				})
				.filter(Boolean);

			if (!section_items.length) return;

			const set_active_by_id = (active_id) => {
				nav_links.forEach((link) => {
					link.classList.toggle(`is-active`, link_to_id(link) === active_id);
				});
			};

			const visible_map = new Map();

			const pick_active_from_visible = () => {
				const header_offset = get_header_offset();

				const visible_items = section_items.filter(({ section_el }) =>
					visible_map.get(section_el)
				);

				if (!visible_items.length) {
					set_active_by_id(null);
					return;
				}

				let nav_item = null;
				let nav_distance = Infinity;

				for (const item of visible_items) {
					const rect = item.section_el.getBoundingClientRect();
					const distance = Math.abs(rect.top - header_offset);

					if (distance < nav_distance) {
						nav_distance = distance;
						nav_item = item;
					}
				}

				set_active_by_id(nav_item ? nav_item.target_id : null);
			};

			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						visible_map.set(entry.target, entry.isIntersecting);
					});

					pick_active_from_visible();
				},
				{
					rootMargin: `-${get_header_offset()}px 0px -60% 0px`,
					threshold: 0,
				}
			);

			section_items.forEach(({ section_el }) => {
				visible_map.set(section_el, false);
				observer.observe(section_el);
			});

			pick_active_from_visible();

			window.addEventListener(
				`resize`,
				() => {
					pick_active_from_visible();
				},
				{ passive: true }
			);
		}

		connectedCallback() {
			this.applyOffset();
			this.initLogoSwap();
			this.observeScroll();

			this.apply_offset_bound = this.applyOffset.bind(this);
			window.addEventListener(`resize`, this.apply_offset_bound, {
				passive: true,
			});

			const mobile_wrapper = this.querySelector(
				`.ah-main-bar-nav-items-mobile-wrapper`
			);
			if (!mobile_wrapper) return;

			let last_scroll_y = window.scrollY || 0;
			let last_dir = 0;
			let lock_until = 0;
			let ticking = false;

			const set_dir = (dir) => {
				if (dir === last_dir) return;

				this.classList.toggle(`is-scrolling-down`, dir === 1);
				this.classList.toggle(`is-scrolling-up`, dir === -1);

				last_dir = dir;
				lock_until = performance.now() + 220;
			};

			const update = () => {
				const now = performance.now();
				const scroll_y = window.scrollY || 0;
				const delta = scroll_y - last_scroll_y;

				if (scroll_y <= 4) {
					set_dir(-1);
					last_scroll_y = scroll_y;
					ticking = false;
					return;
				}

				if (now < lock_until) {
					last_scroll_y = scroll_y;
					ticking = false;
					return;
				}

				if (Math.abs(delta) >= 12) {
					set_dir(delta > 0 ? 1 : -1);
					last_scroll_y = scroll_y;
				}

				ticking = false;
			};

			window.addEventListener(
				`scroll`,
				() => {
					if (ticking) return;
					ticking = true;
					requestAnimationFrame(update);
				},
				{ passive: true }
			);

			this.classList.add(`is-scrolling-up`);
			this.classList.remove(`is-scrolling-down`);
		}
	}

	customElements.define(`after-hours-header`, AfterHoursHeader);
}
